import React from 'react'
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Grid,
  Alert,
  Switch,
  FormControlLabel,
  Chip,
  Paper,
  List,
  ListItem,
  ListItemText,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material'
import {
  Settings as SettingsIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  ExpandMore as ExpandMoreIcon,
  Warning as WarningIcon,
  PlayArrow as PlayArrowIcon,
  Schedule as ScheduleIcon,
  Sync as SyncIcon
} from '@mui/icons-material'
import {
  useSettings,
  useUpdateSettings,
  useFailureReport,
  useCheckFailures,
  useAutoApplyStatus,
  useRunAutoApply
} from '@/hooks/useApi'

const Settings: React.FC = () => {

  // Settings hooks
  const { data: settingsData, isLoading: settingsLoading } = useSettings()
  const updateSettingsMutation = useUpdateSettings()
  const { data: failureReportData } = useFailureReport()
  const checkFailuresMutation = useCheckFailures()

  // Auto-apply hooks
  const { data: autoApplyStatusData, isLoading: autoApplyLoading } = useAutoApplyStatus()
  const runAutoApplyMutation = useRunAutoApply()
  const autoApplyStatus = autoApplyStatusData?.data

  const systemSettings = settingsData?.data
  const failureReport = failureReportData?.data





  // TODO: Handlers pour les paramètres système (non utilisés actuellement)
  /*
  const handleSaveSystemSettings = async () => {
    if (!systemSettings) return

    const updatedSettings = {
      maxConsecutiveFailures: systemSettings.maxConsecutiveFailures,
      enableAutoDisabling: systemSettings.enableAutoDisabling,
      checkIntervalMinutes: systemSettings.checkIntervalMinutes
    }

    updateSettingsMutation.mutate(updatedSettings)
  }

  const handleSystemSettingChange = (field: string, value: any) => {
    console.log('System setting change:', field, value)
  }
  */

  const handleCheckFailures = () => {
    checkFailuresMutation.mutate()
  }


  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
        <SettingsIcon sx={{ mr: 2, fontSize: 32 }} />
        <Typography variant="h4" sx={{ fontWeight: 600 }}>
          Administration
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {/* Paramètres de fiabilité des agents */}
        <Grid item xs={12} lg={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>
                🛑 Fiabilité des agents
              </Typography>

              <Alert severity="info" sx={{ mb: 2 }}>
                Configuration de la désactivation automatique des agents en échec répété.
              </Alert>

              {settingsLoading ? (
                <Typography>Chargement des paramètres...</Typography>
              ) : systemSettings ? (
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={systemSettings.enableAutoDisabling}
                          onChange={(e) =>
                            updateSettingsMutation.mutate({ enableAutoDisabling: e.target.checked })
                          }
                        />
                      }
                      label="Activer la désactivation automatique des agents"
                    />
                  </Grid>

                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      label="Échecs consécutifs max"
                      type="number"
                      value={systemSettings.maxConsecutiveFailures}
                      onChange={(e) => {
                        const value = parseInt(e.target.value) || 3
                        updateSettingsMutation.mutate({ maxConsecutiveFailures: value })
                      }}
                      InputProps={{ inputProps: { min: 1, max: 10 } }}
                      helperText="Nombre d'échecs avant désactivation"
                    />
                  </Grid>

                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      label="Intervalle de vérification (min)"
                      type="number"
                      value={systemSettings.checkIntervalMinutes}
                      onChange={(e) => {
                        const value = parseInt(e.target.value) || 5
                        updateSettingsMutation.mutate({ checkIntervalMinutes: value })
                      }}
                      InputProps={{ inputProps: { min: 1, max: 60 } }}
                      helperText="Fréquence de vérification périodique"
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Button
                      variant="outlined"
                      onClick={handleCheckFailures}
                      disabled={checkFailuresMutation.isPending}
                      fullWidth
                    >
                      {checkFailuresMutation.isPending ? 'Vérification...' : 'Vérifier les agents maintenant'}
                    </Button>
                  </Grid>

                  {failureReport && (
                    <Grid item xs={12}>
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="subtitle2" gutterBottom>
                          📋 Rapport d'échecs
                        </Typography>
                        <Grid container spacing={1}>
                          <Grid item xs={4}>
                            <Paper sx={{ p: 1, textAlign: 'center' }}>
                              <Typography variant="h6" color="error">
                                {failureReport.summary.agentsAtRisk}
                              </Typography>
                              <Typography variant="caption">
                                À risque
                              </Typography>
                            </Paper>
                          </Grid>
                          <Grid item xs={4}>
                            <Paper sx={{ p: 1, textAlign: 'center' }}>
                              <Typography variant="h6" color="warning.main">
                                {failureReport.summary.agentsWithWarnings}
                              </Typography>
                              <Typography variant="caption">
                                Avertissement
                              </Typography>
                            </Paper>
                          </Grid>
                          <Grid item xs={4}>
                            <Paper sx={{ p: 1, textAlign: 'center' }}>
                              <Typography variant="h6" color="info.main">
                                {failureReport.summary.totalAgentsWithFailures}
                              </Typography>
                              <Typography variant="caption">
                                Avec échecs
                              </Typography>
                            </Paper>
                          </Grid>
                        </Grid>
                      </Box>
                    </Grid>
                  )}
                </Grid>
              ) : (
                <Typography color="error">Impossible de charger les paramètres</Typography>
              )}
            </CardContent>
          </Card>
        </Grid>



        {/* Application automatique des mises à jour */}
        <Grid item xs={12} lg={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>
                <SyncIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Application automatique des mises à jour
              </Typography>

              <Alert severity="info" sx={{ mb: 2 }}>
                Configure l'application automatique périodique des mises à jour en attente (PENDING).
              </Alert>

              {autoApplyLoading || settingsLoading ? (
                <Typography>Chargement des paramètres...</Typography>
              ) : (
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={(systemSettings as any)?.enableAutoApplyUpdates || false}
                          onChange={(e) =>
                            updateSettingsMutation.mutate({ enableAutoApplyUpdates: e.target.checked } as any)
                          }
                        />
                      }
                      label="Activer l'application automatique"
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Intervalle d'exécution (minutes)"
                      type="number"
                      value={(systemSettings as any)?.autoApplyIntervalMinutes || 60}
                      onChange={(e) => {
                        const value = parseInt(e.target.value) || 60
                        if (value >= 5 && value <= 1440) {
                          updateSettingsMutation.mutate({ autoApplyIntervalMinutes: value } as any)
                        }
                      }}
                      InputProps={{ inputProps: { min: 5, max: 1440 } }}
                      helperText="Entre 5 minutes et 24 heures (1440 min)"
                      disabled={!(systemSettings as any)?.enableAutoApplyUpdates}
                    />
                  </Grid>

                  {autoApplyStatus && (
                    <>
                      <Grid item xs={12}>
                        <Paper sx={{ p: 2, bgcolor: 'background.default' }}>
                          <Grid container spacing={1}>
                            <Grid item xs={6}>
                              <Typography variant="caption" color="text.secondary">
                                Statut du scheduler
                              </Typography>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                                <Chip
                                  size="small"
                                  icon={autoApplyStatus.isSchedulerRunning ? <ScheduleIcon /> : undefined}
                                  label={autoApplyStatus.isSchedulerRunning ? 'Actif' : 'Inactif'}
                                  color={autoApplyStatus.isSchedulerRunning ? 'success' : 'default'}
                                />
                                {autoApplyStatus.isCurrentlyApplying && (
                                  <Chip size="small" label="En cours..." color="warning" />
                                )}
                              </Box>
                            </Grid>

                            <Grid item xs={6}>
                              <Typography variant="caption" color="text.secondary">
                                Prochaine exécution
                              </Typography>
                              <Typography variant="body2">
                                {autoApplyStatus.nextRunAt
                                  ? new Date(autoApplyStatus.nextRunAt).toLocaleString()
                                  : '-'}
                              </Typography>
                            </Grid>

                            {autoApplyStatus.lastRunAt && (
                              <>
                                <Grid item xs={6}>
                                  <Typography variant="caption" color="text.secondary">
                                    Dernière exécution
                                  </Typography>
                                  <Typography variant="body2">
                                    {new Date(autoApplyStatus.lastRunAt).toLocaleString()}
                                  </Typography>
                                </Grid>

                                <Grid item xs={6}>
                                  <Typography variant="caption" color="text.secondary">
                                    Résultat
                                  </Typography>
                                  {autoApplyStatus.lastRunResult && (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                                      <Chip
                                        size="small"
                                        icon={<SuccessIcon />}
                                        label={`${autoApplyStatus.lastRunResult.success} OK`}
                                        color="success"
                                        variant="outlined"
                                      />
                                      {autoApplyStatus.lastRunResult.failed > 0 && (
                                        <Chip
                                          size="small"
                                          icon={<ErrorIcon />}
                                          label={`${autoApplyStatus.lastRunResult.failed} échec`}
                                          color="error"
                                          variant="outlined"
                                        />
                                      )}
                                    </Box>
                                  )}
                                </Grid>
                              </>
                            )}
                          </Grid>
                        </Paper>
                      </Grid>

                      {autoApplyStatus.lastRunResult && autoApplyStatus.lastRunResult.errors.length > 0 && (
                        <Grid item xs={12}>
                          <Accordion>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                              <Typography variant="body2" color="error">
                                <WarningIcon sx={{ fontSize: 16, mr: 1, verticalAlign: 'middle' }} />
                                {autoApplyStatus.lastRunResult.errors.length} erreur(s) lors de la dernière exécution
                              </Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                              <List dense>
                                {autoApplyStatus.lastRunResult.errors.slice(0, 5).map((error, index) => (
                                  <ListItem key={index}>
                                    <ListItemText
                                      primary={error}
                                      primaryTypographyProps={{ variant: 'caption', color: 'error' }}
                                    />
                                  </ListItem>
                                ))}
                              </List>
                            </AccordionDetails>
                          </Accordion>
                        </Grid>
                      )}
                    </>
                  )}

                  <Grid item xs={12}>
                    <Button
                      variant="outlined"
                      startIcon={<PlayArrowIcon />}
                      onClick={() => runAutoApplyMutation.mutate()}
                      disabled={runAutoApplyMutation.isPending || autoApplyStatus?.isCurrentlyApplying}
                      fullWidth
                    >
                      {runAutoApplyMutation.isPending || autoApplyStatus?.isCurrentlyApplying
                        ? 'Application en cours...'
                        : 'Exécuter maintenant'}
                    </Button>
                  </Grid>
                </Grid>
              )}
            </CardContent>
          </Card>
        </Grid>



        {/* Rapport détaillé des échecs */}
        {failureReport && failureReport.agents.length > 0 && (
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  🚨 Agents en échec
                </Typography>

                <Alert severity="warning" sx={{ mb: 2 }}>
                  {failureReport.summary.agentsAtRisk} agent(s) risque(nt) d'être désactivé(s) automatiquement.
                </Alert>

                {failureReport.agents.map((agent) => (
                  <Accordion key={agent.agentId} sx={{ mb: 1 }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="subtitle1">
                            {agent.agentName}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            ID: {agent.agentId}
                          </Typography>
                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Chip
                            icon={agent.shouldDisable ? <ErrorIcon /> : <WarningIcon />}
                            label={`${agent.consecutiveFailures} échec(s)`}
                            color={agent.shouldDisable ? 'error' : 'warning'}
                            size="small"
                          />
                          {agent.shouldDisable && (
                            <Chip
                              label="À désactiver"
                              color="error"
                              variant="outlined"
                              size="small"
                            />
                          )}
                        </Box>
                      </Box>
                    </AccordionSummary>

                    <AccordionDetails>
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                          <Typography variant="subtitle2" gutterBottom>
                            📋 Informations
                          </Typography>
                          <List dense>
                            <ListItem>
                              <ListItemText
                                primary="Échecs consécutifs"
                                secondary={`${agent.consecutiveFailures} / ${failureReport.settings.maxConsecutiveFailures}`}
                              />
                            </ListItem>
                            <ListItem>
                              <ListItemText
                                primary="Dernier échec"
                                secondary={new Date(agent.lastFailureAt).toLocaleString()}
                              />
                            </ListItem>
                            <ListItem>
                              <ListItemText
                                primary="Statut"
                                secondary={
                                  agent.shouldDisable
                                    ? "🔴 Sera désactivé automatiquement"
                                    : "🟡 Surveillé"
                                }
                              />
                            </ListItem>
                          </List>
                        </Grid>

                        <Grid item xs={12} md={6}>
                          <Typography variant="subtitle2" gutterBottom>
                            📅 Derniers échecs
                          </Typography>
                          <List dense>
                            {agent.recentRuns.slice(0, 3).map((run) => (
                              <ListItem key={run.id}>
                                <ListItemText
                                  primary={new Date(run.startedAt).toLocaleString()}
                                  secondary={
                                    <Box>
                                      <Typography variant="caption" color="error">
                                        {run.error?.substring(0, 100)}
                                        {run.error && run.error.length > 100 && '...'}
                                      </Typography>
                                    </Box>
                                  }
                                />
                              </ListItem>
                            ))}
                          </List>
                        </Grid>
                      </Grid>
                    </AccordionDetails>
                  </Accordion>
                ))}
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>

    </Box>
  )
}

export default Settings
